import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FileCode,
  ChevronDown,
  ChevronUp,
  Copy,
  CheckCircle,
  Award
} from 'lucide-react';
import QuestionHtmlWithZoom from './QuestionHtmlWithZoom';

interface Question {
  id: string;
  type: string;
  title: string;
  score: number;
  referenceAnswer?: string;
  scoringPoints?: string;
}

interface SubjectiveTextResultDetailProps {
  question: Question;
  answer: string;
  teacherFeedback?: { score?: number; feedback?: string };
}

export default function SubjectiveTextResultDetail({
  question,
  answer,
  teacherFeedback,
}: SubjectiveTextResultDetailProps) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    answer: true,
    grading: true,
    reference: true,
    rules: true,
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('复制失败:', err);
    }
  };

  // 统一评分判断逻辑
  const isGraded = teacherFeedback?.score != null;
  const displayScore = teacherFeedback?.score ?? 0;
  const maxScore = question.score;
  const displayFeedback = teacherFeedback?.feedback ?? '';

  return (
    <div className="space-y-4">
      {/* 你的答案区 */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl border border-gray-200 overflow-hidden"
      >
        <button
          onClick={() => toggleSection('answer')}
          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <FileCode className="w-5 h-5 text-green-600" />
            <span className="font-medium text-gray-900">你的答案</span>
          </div>
          {expandedSections.answer ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>
        {expandedSections.answer && (
          <div className="p-4">
            {answer ? (
              <div className="relative">
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <pre className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                    {answer}
                  </pre>
                </div>
                <button
                  onClick={() => copyToClipboard(answer)}
                  className="absolute top-2 right-2 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title="复制答案"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <FileCode className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500">暂无作答内容</p>
              </div>
            )}
          </div>
        )}
      </motion.div>

      {/* 评分结果区 */}
      {isGraded && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl border border-gray-200 overflow-hidden"
        >
          <button
            onClick={() => toggleSection('grading')}
            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Award className="w-5 h-5 text-yellow-600" />
              <span className="font-medium text-gray-900">评分结果</span>
            </div>
            {expandedSections.grading ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          {expandedSections.grading && (
            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-blue-600" />
                  <div>
                    <p className="text-sm text-gray-600">得分</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {displayScore}/{maxScore}分
                    </p>
                  </div>
                </div>
              </div>

              {displayFeedback && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-2">评语</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{displayFeedback}</p>
                </div>
              )}
            </div>
          )}
        </motion.div>
      )}

      {/* 参考答案区 */}
      {question.referenceAnswer && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl border border-gray-200 overflow-hidden"
        >
          <button
            onClick={() => toggleSection('reference')}
            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-purple-600" />
              <span className="font-medium text-gray-900">参考答案</span>
            </div>
            {expandedSections.reference ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          {expandedSections.reference && (
            <div className="p-4">
              <QuestionHtmlWithZoom 
                html={question.referenceAnswer || '<p>暂无参考答案说明</p>'} 
              />
            </div>
          )}
        </motion.div>
      )}

      {/* 评分规则区 */}
      {question.scoringPoints && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl border border-gray-200 overflow-hidden"
        >
          <button
            onClick={() => toggleSection('rules')}
            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Award className="w-5 h-5 text-orange-600" />
              <span className="font-medium text-gray-900">评分规则</span>
            </div>
            {expandedSections.rules ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          {expandedSections.rules && (
            <div className="p-4">
              <QuestionHtmlWithZoom 
                html={question.scoringPoints || '<p>暂无评分规则说明</p>'} 
              />
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}
